import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Cross2Icon, Pencil1Icon } from "@radix-ui/react-icons";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import CircleDollar from "~/hec/assets/icons/circle-dollar";
import {
  barState,
  parseBalanceInput,
  remainingCents,
} from "~/hec/components/fsa-funds/funds";
import BalanceProgressBar from "~/hec/components/tpa-balance/balance-progress-bar";
import { useFormatPrice } from "~/shop/utils/price";

interface SpenderBalanceProps {
  /** Null when no balance is set. */
  balanceCents: number | null;
  cartCents: number;
  /** False until the cart is known: the amount is a skeleton, never balance - 0. */
  known: boolean;
  /** "No thanks" was pressed: the card shows its pencil, no amount (D10). */
  dismissed: boolean;
  onSubmit: (cents: number) => void;
  onClear: () => void;
  onDismiss: () => void;
}

/**
 * COM-258 balance entry: prompt, display (remaining funds + pencil + bar) and
 * edit. The pencil edits the balance, not the remaining (C3).
 */
export function SpenderBalance({
  balanceCents,
  cartCents,
  known,
  dismissed,
  onSubmit,
  onClear,
  onDismiss,
}: SpenderBalanceProps) {
  const intl = useIntl();
  const formatPrice = useFormatPrice();
  const errorId = useId();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [cleared, setCleared] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prompting = balanceCents === null && !dismissed && !editing;
  const inputRef = useRef<HTMLInputElement>(null);
  const editRef = useRef<HTMLButtonElement>(null);
  // Set by user actions only, so mount and parent-driven changes never steal focus.
  const focusNext = useRef<"input" | "edit" | null>(null);
  useEffect(() => {
    if (!focusNext.current) return;
    (focusNext.current === "input" ? inputRef : editRef).current?.focus();
    focusNext.current = null;
  });

  const errors = {
    empty: intl.formatMessage({
      id: "spenderBalance.error_empty",
      defaultMessage: "Enter your FSA balance.",
    }),
    negative: intl.formatMessage({
      id: "spenderBalance.error_negative",
      defaultMessage: "Enter an amount of $0 or more.",
    }),
    invalid: intl.formatMessage({
      id: "spenderBalance.error_invalid",
      defaultMessage: "Enter a dollar amount, like 250 or $1,000.00.",
    }),
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseBalanceInput(text);
    // An empty or "0" Update clears the balance (D11); only the prompt demands a value.
    if (editing && !parsed.ok && parsed.error === "empty")
      return done(onClear, "input");
    if (!parsed.ok) return setError(errors[parsed.error]);
    const clears = editing && parsed.cents === 0;
    done(
      clears ? onClear : () => onSubmit(parsed.cents),
      clears ? "input" : "edit",
    );
  };
  const done = (then: () => void, focus: "input" | "edit") => {
    focusNext.current = focus;
    setEditing(false);
    setError(null);
    then();
  };
  const startEdit = () => {
    setText(balanceCents === null ? "" : String(balanceCents / 100));
    setCleared(false);
    setError(null);
    focusNext.current = "input";
    setEditing(true);
  };

  const fsaBalanceText = intl.formatMessage({
    id: "spenderBalance.placeholder",
    defaultMessage: "Add Your FSA Balance",
  });

  if (prompting || editing) {
    return (
      <form
        noValidate
        onSubmit={submit}
        data-testid="spender-balance-form"
        className="mb-4 flex flex-col gap-2"
      >
        <label
          aria-label={fsaBalanceText}
          className="text-sm font-light text-blue-02"
        >
          {fsaBalanceText}

          <div className="relative">
            <CircleDollar
              aria-hidden
              data-testid="spender-balance-input-icon"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-blue-02"
            />
            <Input
              ref={inputRef}
              data-testid="spender-balance-input"
              inputMode="decimal"
              autoComplete="off"
              value={text}
              onChange={e => {
                setText(e.target.value);
                setCleared(false);
              }}
              placeholder={`0.00`}
              aria-invalid={error !== null}
              aria-describedby={error ? errorId : undefined}
              className={editing ? "pl-11 pr-9" : "pl-11"}
            />
            <button
              type="reset"
              onClick={() => {
                setText("");
                setCleared(true);
                inputRef.current?.focus();
              }}
              data-testid="spender-balance-clear-input"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
            >
              <Cross2Icon aria-hidden />
              <span className="sr-only">
                <FormattedMessage
                  id="spenderBalance.clear_input"
                  defaultMessage="Clear amount"
                />
              </span>
            </button>
          </div>
        </label>

        <p
          role="status"
          data-testid="spender-balance-cleared"
          className="text-sm font-light text-gray-03 empty:sr-only"
        >
          {cleared && (
            <FormattedMessage
              id="spenderBalance.cleared"
              defaultMessage="Your balance has been cleared."
            />
          )}
        </p>

        {error && (
          <p
            id={errorId}
            role="alert"
            data-testid="spender-balance-error"
            className="text-sm text-error"
          >
            {error}
          </p>
        )}

        <div className="flex justify-between">
          {editing ? (
            <Button
              type="button"
              variant="link"
              size="2"
              data-testid="spender-balance-cancel"
              onClick={() => done(() => {}, "edit")}
            >
              <FormattedMessage
                id="spenderBalance.cancel"
                defaultMessage="Cancel"
              />
            </Button>
          ) : (
            <Button
              type="button"
              variant="link"
              size="2"
              data-testid="spender-balance-no-thanks"
              onClick={() => {
                focusNext.current = "edit";
                onDismiss();
              }}
            >
              <FormattedMessage
                id="spenderBalance.no_thanks"
                defaultMessage="No thanks"
              />
            </Button>
          )}
          <Button
            type="submit"
            variant="cta"
            size="2"
            data-testid="spender-balance-submit"
          >
            {editing ? (
              <FormattedMessage
                id="spenderBalance.update"
                defaultMessage="Update"
              />
            ) : (
              <FormattedMessage
                id="spenderBalance.submit"
                defaultMessage="Submit"
              />
            )}
          </Button>
        </div>
      </form>
    );
  }

  // No thanks (no balance): an empty bar, never a skeleton.
  const bar =
    balanceCents === null
      ? { pct: 0, over: false }
      : barState(balanceCents, cartCents);
  return (
    <div data-testid="spender-balance-display" className="mb-4">
      <div className="flex items-center gap-2 text-blue-03">
        <FormattedMessage
          id="spenderBalance.remaining"
          defaultMessage="Remaining funds:"
        />
        <button
          type="button"
          ref={editRef}
          data-testid="spender-balance-edit"
          onClick={startEdit}
          className="p-1"
        >
          <Pencil1Icon aria-hidden />
          <span className="sr-only">
            <FormattedMessage
              id="spenderBalance.edit"
              defaultMessage="Edit your FSA balance"
            />
          </span>
        </button>
      </div>
      {balanceCents !== null &&
        (known ? (
          <p
            data-testid="spender-balance-remaining"
            className="text-lg font-medium"
          >
            {formatPrice(
              Math.max(0, remainingCents(balanceCents, cartCents)) / 100,
            )}
          </p>
        ) : (
          <Skeleton
            data-testid="spender-balance-remaining-skeleton"
            className="h-6 w-20"
          />
        ))}
      {(known || balanceCents === null) && (
        <div data-testid="spender-balance-bar" data-over={bar.over}>
          <BalanceProgressBar
            variant="solid"
            percentage={bar.pct}
            leftToSpend=""
            available=""
            showAvailableToSpend={false}
            bgColor={bar.over ? "bg-error" : "bg-primary"}
          />
        </div>
      )}
      <p
        data-testid="spender-ribbon-disclaimer-message"
        className="mt-4 text-xs font-light"
      >
        <FormattedMessage
          id="spenderRibbon.disclaimer"
          defaultMessage="Shipping fees, coupons, and taxes are not included."
        />
      </p>
    </div>
  );
}
